import { useState, useEffect } from 'react'
import {
  Download,
  Calendar,
  TrendingUp,
  Users,
  Gift,
  Activity,
  BarChart3,
  PieChart as PieChartIcon,
  Loader2,
  Shield,
  X,
  Building2
} from 'lucide-react'
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts'
import { statisticsApi, giftsApi } from '../../lib/api'

// ========== PDF / Excel 导出辅助函数 ==========

function downloadBlob(data: Blob, filename: string) {
  const url = URL.createObjectURL(data)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}

/**
 * 截取前 N 名，剩余项合并为「其他」
 * - 按 value 字段降序
 * - 其他项的 value 是所有剩余项的累加
 * - 同时按比例累加 count（避免饼图 tooltip 显示「其他=0」）
 */
function mergeTopN<T extends { value: number; name: string }>(items: T[], n: number, otherName = '其他', countKey: 'count' | null = null): T[] {
  if (items.length <= n) return items
  const top = items.slice(0, n)
  const rest = items.slice(n)
  const otherValue = rest.reduce((sum, it) => sum + (it.value || 0), 0)
  const other: any = { ...(rest[0] as T), name: otherName, value: otherValue }
  if (countKey) {
    other[countKey] = rest.reduce((sum, it: any) => sum + ((it as any)[countKey!] || 0), 0)
  }
  return [...top, other as T]
}

function formatLocalDate(date: Date): string {
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const d = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${d}`
}

function computeDateRange(range: string, customStart?: string, customEnd?: string): { start_date: string; end_date: string } {
  const today = new Date()
  switch (range) {
    case 'week': {
      const start = new Date(today)
      start.setDate(start.getDate() - 6)
      const end = new Date(today)
      end.setDate(end.getDate() + 1)
      return { start_date: formatLocalDate(start), end_date: formatLocalDate(end) }
    }
    case 'month': {
      const start = new Date(today.getFullYear(), today.getMonth(), 1)
      const end = new Date(today.getFullYear(), today.getMonth() + 1, 1)
      return { start_date: formatLocalDate(start), end_date: formatLocalDate(end) }
    }
    case 'quarter': {
      const q = Math.floor(today.getMonth() / 3)
      const start = new Date(today.getFullYear(), q * 3, 1)
      const end = new Date(today.getFullYear(), q * 3 + 3, 1)
      return { start_date: formatLocalDate(start), end_date: formatLocalDate(end) }
    }
    case 'year': {
      const start = new Date(today.getFullYear(), 0, 1)
      const end = new Date(today.getFullYear() + 1, 0, 1)
      return { start_date: formatLocalDate(start), end_date: formatLocalDate(end) }
    }
    case 'custom':
    default: {
      if (customStart && customEnd) {
        const endDate = new Date(customEnd)
        endDate.setDate(endDate.getDate() + 1)
        return { start_date: customStart, end_date: formatLocalDate(endDate) }
      }
      const end = new Date(today)
      end.setDate(end.getDate() + 1)
      return { start_date: customStart || formatLocalDate(today), end_date: formatLocalDate(end) }
    }
  }
}

function getExportPeriods(): Array<{ label: string; start_date: string; end_date: string }> {
  const today = new Date()
  const fmt = formatLocalDate
  const weekStart = new Date(today)
  weekStart.setDate(weekStart.getDate() - 6)
  const weekEnd = new Date(today)
  weekEnd.setDate(weekEnd.getDate() + 1)
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1)
  const monthEnd = new Date(today.getFullYear(), today.getMonth() + 1, 1)
  const q = Math.floor(today.getMonth() / 3)
  const quarterStart = new Date(today.getFullYear(), q * 3, 1)
  const quarterEnd = new Date(today.getFullYear(), q * 3 + 3, 1)
  const yearStart = new Date(today.getFullYear(), 0, 1)
  const yearEnd = new Date(today.getFullYear() + 1, 0, 1)
  return [
    { label: '近7天', start_date: fmt(weekStart), end_date: fmt(weekEnd) },
    { label: '当月', start_date: fmt(monthStart), end_date: fmt(monthEnd) },
    { label: '当季度', start_date: fmt(quarterStart), end_date: fmt(quarterEnd) },
    { label: '本年度', start_date: fmt(yearStart), end_date: fmt(yearEnd) },
  ]
}

async function exportPdf(): Promise<void> {
  const token = localStorage.getItem('token')
  const res = await fetch('/api/v1/statistics/export/pdf', {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  })
  if (!res.ok) throw new Error('PDF 生成失败')
  const blob = await res.blob()
  downloadBlob(blob, `数据统计_${new Date().toISOString().slice(0, 10)}.pdf`)
}

async function exportExcel(): Promise<void> {
  const periods = getExportPeriods()
  const allData = await Promise.all(
    periods.map(p =>
      Promise.all([
        statisticsApi.usersOverview({ start_date: p.start_date, end_date: p.end_date }),
        statisticsApi.pointsOverview({ start_date: p.start_date, end_date: p.end_date }),
        statisticsApi.exchangesOverview({ start_date: p.start_date, end_date: p.end_date }),
        statisticsApi.pointsByEvent({ start_date: p.start_date, end_date: p.end_date }),
        statisticsApi.hospitalRanking({ start_date: p.start_date, end_date: p.end_date }),
        statisticsApi.pointsDaily({ start_date: p.start_date, end_date: p.end_date }),
      ]).then(([usersData, pointsData, exchangesData, eventsData, hospitalData, dailyData]) => ({
        label: p.label,
        totalUsers: (usersData as any)?.total_users ?? 0,
        activeUsers: (usersData as any)?.active_users ?? 0,
        monthPoints: (pointsData as any)?.month_points ?? 0,
        todayPoints: (pointsData as any)?.today_points ?? 0,
        totalBalance: (pointsData as any)?.total_balance ?? 0,
        pendingPoints: (pointsData as any)?.pending_points ?? 0,
        exchangesCount: (exchangesData as any)?.total_points ?? 0,
        pendingExchanges: (exchangesData as any)?.pending_count ?? 0,
        topEvents: ((eventsData as any)?.items || eventsData || []).slice(0, 5),
        hospitalStats: (((hospitalData as any)?.items || hospitalData || []) as any[]).map((h: any) => ({
          name: h.hospital_name || h.name || '', value: h.total_points || 0
        })),
        dailyData: (((dailyData as any)?.items || dailyData || []) as any[]).map((d: any) => ({ date: d.date, points: d.points, count: d.count })),
      }))
    )
  )

  const rows: string[][] = []
  rows.push(['眉山疾控积分系统 - 数据统计报表', '', '', '', ''])
  rows.push([`导出时间：${new Date().toLocaleString('zh-CN')}`, '', '', '', ''])
  rows.push(['', '', '', ''])

  allData.forEach(p => {
    rows.push([`【${p.label}】`, '', '', '', ''])
    rows.push(['指标', '数值', '指标', '数值'])
    rows.push(['总用户数', String(p.totalUsers), '活跃用户', String(p.activeUsers)])
    rows.push(['今日新增积分', String(p.todayPoints), '周期内积分', String(p.monthPoints)])
    rows.push(['总积分余额', String(p.totalBalance), '待确认积分', String(p.pendingPoints)])
    rows.push(['兑换积分合计', String(p.exchangesCount), '待处理兑换', String(p.pendingExchanges)])
    rows.push(['事件类型明细', '', '', ''])
    rows.push(['事件名称', '总积分', '记录数', ''])
    p.topEvents.forEach((e: any) => rows.push([e.event_type_name || e.name || '-', String(e.total_points ?? 0), String(e.record_count ?? 0), '']))
    rows.push(['机构积分分布', '', '', ''])
    rows.push(['机构名称', '总积分', '机构名称', '总积分'])
    const half = Math.ceil(p.hospitalStats.length / 2)
    for (let i = 0; i < half; i++) {
      const a = p.hospitalStats[i] || { name: '', value: '' }
      const b = p.hospitalStats[i + half] || { name: '', value: '' }
      rows.push([a.name, String(a.value), b.name, String(b.value)])
    }
    rows.push(['', '', '', ''])
  })

  const csvContent = rows.map(row =>
    row.map(cell => `"${String(cell).replace(/"/g, '')}"`).join(',')
  ).join('\n')
  const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
  downloadBlob(blob, `数据统计_${new Date().toISOString().slice(0, 10)}.csv`)
}

// 县区 × 事件 积分矩阵表格（横向可滑动）
const MatrixSection = ({ title, matrix }: { title: string; matrix: any }) => {
  if (!matrix) return null
  const events: Array<{ id: string; name: string }> = matrix.events || []
  const rows: any[] = matrix.rows || []
  if (events.length === 0 || rows.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-md p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4">{title}</h3>
        <p className="text-center text-gray-400 py-8">暂无数据</p>
      </div>
    )
  }

  // 按机构分组，方便渲染 rowspan
  const grouped: { hospital_id: string; hospital_name: string; items: any[] }[] = []
  let current: any = null
  rows.forEach((r: any) => {
    if (!current || current.hospital_id !== r.hospital_id) {
      current = { hospital_id: r.hospital_id, hospital_name: r.hospital_name, items: [] }
      grouped.push(current)
    }
    current.items.push(r)
  })

  const shortName = (name: string) => (name || '').slice(0, 6)

  return (
    <div className="bg-white rounded-2xl shadow-md p-6 overflow-hidden">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-bold text-gray-800">{title}</h3>
        <span className="text-xs text-gray-500 flex items-center gap-1">
          <span>←</span> 横向滑动查看更多事件 <span>→</span>
        </span>
      </div>
      <div
        className="overflow-auto rounded-lg border border-gray-200"
        style={{ maxHeight: '480px', width: '100%', minWidth: 0 }}
      >
        <table
          className="text-sm border-collapse"
          style={{ width: `${280 + events.length * 140}px`, tableLayout: 'fixed' }}
        >
          <thead className="sticky top-0 z-10 bg-gray-50 shadow-sm">
            <tr>
              <th className="sticky left-0 z-20 bg-gray-50 px-3 py-3 text-left font-semibold text-gray-700 border-b border-r border-gray-200" style={{ width: '100px' }}>机构</th>
              <th className="sticky left-[100px] z-20 bg-gray-50 px-3 py-3 text-left font-semibold text-gray-700 border-b border-r border-gray-200" style={{ width: '80px' }}>人群</th>
              <th className="px-3 py-3 text-right font-semibold text-green-700 border-b border-r border-gray-200" style={{ width: '100px' }}>总积分</th>
              {events.map((e: any) => (
                <th key={e.id} className="px-3 py-3 text-left font-medium text-gray-600 border-b border-gray-200 whitespace-nowrap overflow-hidden text-ellipsis" title={e.name} style={{ width: '140px' }}>
                  {e.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {grouped.map((g, groupIdx) => (
              g.items.map((r: any, rowIdx: number) => (
                <tr
                  key={`${r.hospital_id}-${r.crowd_type}`}
                  className={`hover:bg-green-50/40 ${groupIdx % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'}`}
                >
                  {rowIdx === 0 && (
                    <td
                      rowSpan={g.items.length}
                      className="sticky left-0 z-10 bg-inherit px-3 py-3 font-medium text-gray-800 border-b border-r border-gray-200 whitespace-nowrap"
                      style={{ width: '100px' }}
                    >
                      {shortName(g.hospital_name)}
                    </td>
                  )}
                  <td className="sticky left-[100px] z-10 bg-inherit px-3 py-3 text-gray-700 border-b border-r border-gray-200" style={{ width: '80px' }}>
                    {r.crowd_type}
                  </td>
                  <td className="px-3 py-3 text-right font-bold text-green-700 border-b border-r border-gray-200" style={{ width: '100px' }}>
                    {r.total_points?.toLocaleString() || 0}
                  </td>
                  {events.map((e: any) => {
                    const cell = r.cells?.[e.id]
                    const pts = cell?.points || 0
                    const cnt = cell?.count || 0
                    if (pts === 0 && cnt === 0) {
                      return (
                        <td key={e.id} className="px-3 py-3 text-gray-300 border-b border-gray-200" style={{ width: '140px' }}>
                          —
                        </td>
                      )
                    }
                    return (
                      <td key={e.id} className="px-3 py-3 text-gray-700 border-b border-gray-200" title={`积分 ${pts} / 次数 ${cnt}`} style={{ width: '140px' }}>
                        <div className="font-medium">{pts.toLocaleString()}</div>
                        <div className="text-xs text-gray-400">{cnt} 次</div>
                      </td>
                    )
                  })}
                </tr>
              ))
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ========== 主组件 ==========

const Statistics = () => {
  const [dateRange, setDateRange] = useState('week')
  const [customStart, setCustomStart] = useState(() => {
    const d = new Date()
    d.setDate(d.getDate() - 29)
    return formatLocalDate(d)
  })
  const [customEnd, setCustomEnd] = useState(() => formatLocalDate(new Date()))

  const dateParams = computeDateRange(dateRange, customStart, customEnd)
  const [stats, setStats] = useState<any>({
    totalUsers: 0,
    activeUsers: 0,
    totalPointsIssued: 0,
    totalPointsRedeemed: 0,
    pendingPoints: 0,
    totalGiftStock: 0,
    activeEvents: 0,
    newUsersThisWeek: 0,
    totalPoints: 0,
    totalExchanges: 0,
    pendingExchanges: 0,
    todayPoints: 0,
    weeklyData: [],
    topEvents: [],
    hospitalStats: []
  })
  const [loading, setLoading] = useState(true)
  const [exporting, setExporting] = useState(false)
  // 人群类型统计（累计人数，不受日期区间影响）
  const [crowdStats, setCrowdStats] = useState<any>(null)
  const [hospitalCrowdStats, setHospitalCrowdStats] = useState<any>(null)

  // 县区 × 事件 矩阵（当月 + 累计）
  const [matrixMonth, setMatrixMonth] = useState<any>(null)
  const [matrixCumulative, setMatrixCumulative] = useState<any>(null)
  const [exportingMatrix, setExportingMatrix] = useState(false)
  const COLORS = ['#2B5D3A', '#4A90E2', '#F5A623', '#E74C3C', '#9B59B6']

  // 矩阵导出 Excel（CSV 格式，Excel 友好）
  const exportMatrixExcel = () => {
    if (!matrixMonth || !matrixCumulative) return
    setExportingMatrix(true)
    try {
      const buildSection = (label: string, m: any): string[][] => {
        const events: Array<{ id: string; name: string }> = m.events || []
        const rows: any[] = m.rows || []
        const lines: string[][] = []
        lines.push([`【${label}积分矩阵】`])
        const header = ['机构', '人群', '总积分', '参与人数']
        events.forEach((e: any) => {
          header.push(`${e.name} 积分`)
          header.push(`${e.name} 次数`)
        })
        lines.push(header)
        rows.forEach((r: any) => {
          const line = [
            r.hospital_name,
            r.crowd_type,
            String(r.total_points ?? 0),
            String(r.user_count ?? 0),
          ]
          events.forEach((e: any) => {
            const cell = r.cells?.[e.id]
            line.push(String(cell?.points ?? 0))
            line.push(String(cell?.count ?? 0))
          })
          lines.push(line)
        })
        return lines
      }

      const allLines: string[][] = []
      allLines.push(...buildSection('本月', matrixMonth))
      allLines.push([]) // 空行
      allLines.push(...buildSection('累计', matrixCumulative))

      // 转 CSV：BOM 头避免 Excel 乱码
      const csv = '\ufeff' + allLines
        .map(row => row.map(cell => {
          const s = String(cell ?? '')
          return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
        }).join(','))
        .join('\n')

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const date = new Date().toISOString().slice(0, 10)
      a.href = url
      a.download = `积分矩阵_${date}.csv`
      a.click()
      URL.revokeObjectURL(url)
    } catch (e) {
      console.error('导出失败', e)
      alert('导出失败：' + (e as Error).message)
    } finally {
      setExportingMatrix(false)
    }
  }

  // 机构切换功能
  const [hospitals, setHospitals] = useState<any[]>([])
  const [selectedHospitalId, setSelectedHospitalId] = useState<string | null>(null)
  const [hospitalDrawerOpen, setHospitalDrawerOpen] = useState(false)
  const [hospitalStats, setHospitalStats] = useState<any>(null)
  const [hospitalLoading, setHospitalLoading] = useState(false)

  // 权限判断
  const rawAuthUser = localStorage.getItem('auth_user')
  const authUser = rawAuthUser ? JSON.parse(rawAuthUser) : null
  const isSupervisor = authUser?.role === 'medical' && authUser?.medicalRole === 'supervisor'
  const isAdmin = authUser?.role === 'admin'

  useEffect(() => {
    loadStatistics()
    if (isAdmin) {
      fetch('/api/v1/hospitals', {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      }).then(r => r.json()).then(data => {
        const list = (data as any).hospitals || (data as any).items || data || []
        setHospitals(Array.isArray(list) ? list.filter((h: any) => h.status === 'active') : [])
      }).catch(() => {})
    }
  }, [dateRange, customStart, customEnd])

  // 切换机构时，加载该机构的人群统计
  useEffect(() => {
    if (!selectedHospitalId) {
      setHospitalCrowdStats(null)
      return
    }
    statisticsApi.crowdStats({ hospital_id: selectedHospitalId })
      .then((data: any) => setHospitalCrowdStats(data))
      .catch(() => setHospitalCrowdStats(null))
  }, [selectedHospitalId])

  const loadStatistics = () => {
    setLoading(true)
    Promise.all([
      statisticsApi.usersOverview(dateParams),
      statisticsApi.pointsOverview(dateParams),
      statisticsApi.pointsDaily(dateParams),
      statisticsApi.pointsByEvent(dateParams),
      statisticsApi.exchangesOverview(dateParams),
      statisticsApi.hospitalRanking(dateParams),
      // 人群类型统计：累计人数，不受日期区间影响
      statisticsApi.crowdStatsByHospitals(),
      // 县区 × 事件 矩阵
      statisticsApi.matrixCurrentMonth(),
      statisticsApi.matrixCumulative(),
      giftsApi.list({ page: 1, page_size: 100 }),
    ]).then(([usersRes, pointsRes, dailyRes, eventsRes, exchangesRes, hospitalRes, crowdRes, monthMatrix, cumMatrix, giftsRes]) => {
      const usersData = usersRes || {}
      const pointsData = pointsRes || {}
      const dailyData = (dailyRes as any)?.items || dailyRes || []
      const eventsData = (eventsRes as any)?.items || eventsRes || []
      const exchangesData = exchangesRes || {}
      const hospitalData = (hospitalRes as any)?.items || hospitalRes || []
      const crowdData = crowdRes || []

      // 计算总礼品库存（从gifts列表求和）
      const giftsList = (giftsRes as any)?.gifts || []
      const totalGiftStock = giftsList.reduce((sum: number, g: any) => sum + (g.stock || 0), 0)

      setStats({
        totalUsers: (usersData as any).total_users || 0,
        activeUsers: (usersData as any).active_users || 0,
        totalPointsIssued: (pointsData as any).month_points || 0,
        totalPointsRedeemed: (exchangesData as any).total_points || 0,
        pendingPoints: (pointsData as any).pending_points || 0,
        totalGiftStock,
        activeEvents: eventsData.length || 0,
        newUsersThisWeek: (usersData as any).month_new_users || 0,
        totalPoints: (pointsData as any).total_balance || 0,
        totalExchanges: (exchangesData as any).month_count || 0,
        pendingExchanges: (exchangesData as any).pending_count || 0,
        todayPoints: (pointsData as any).today_points || 0,
        weeklyData: dailyData.map((d: any) => ({ date: d.date, points: d.points, count: d.count })),
        topEvents: mergeTopN(
          eventsData.map((e: any) => ({
            name: e.event_type_name || e.name || '',
            count: e.record_count ?? e.count ?? 0,
            value: e.total_points ?? 0
          })),
          9,
          '其他',
          'count'
        ).map((e: any) => ({
          name: e.name,
          count: e.count,
          value: e.value,
          points: e.value
        })),
        hospitalStats: hospitalData.map((h: any) => ({
          name: h.hospital_name || h.name || '',
          value: h.total_points || 0,
          users: h.user_count || 0,
          exchanges: h.exchange_count || 0
        }))
      })
      setCrowdStats(crowdData)
      setMatrixMonth(monthMatrix)
      setMatrixCumulative(cumMatrix)
    }).catch(() => {
      setStats({
        totalUsers: 0, activeUsers: 0, totalPointsIssued: 0, totalPointsRedeemed: 0,
        pendingPoints: 0, totalGiftStock: 0, activeEvents: 0, newUsersThisWeek: 0,
        totalPoints: 0, totalExchanges: 0, pendingExchanges: 0, todayPoints: 0,
        weeklyData: [], topEvents: [], hospitalStats: []
      })
      setCrowdStats(null)
      setMatrixMonth(null)
      setMatrixCumulative(null)
    }).finally(() => setLoading(false))
  }

  const handleExport = (type: 'excel' | 'pdf') => {
    if (exporting) return
    setExporting(true)
    const task = type === 'excel' ? exportExcel() : exportPdf()
    task.catch(() => {}).finally(() => setExporting(false))
  }

  const openHospitalDrawer = async (hospitalId: string) => {
    setSelectedHospitalId(hospitalId)
    setHospitalDrawerOpen(true)
    setHospitalLoading(true)
    try {
      const [usersRes, pointsRes, dailyRes, eventsRes, exchangesRes] = await Promise.all([
        statisticsApi.usersOverview({ ...dateParams, hospital_id: hospitalId } as any),
        statisticsApi.pointsOverview({ ...dateParams, hospital_id: hospitalId } as any),
        statisticsApi.pointsDaily({ ...dateParams, hospital_id: hospitalId } as any),
        statisticsApi.pointsByEvent({ ...dateParams, hospital_id: hospitalId } as any),
        statisticsApi.exchangesOverview({ ...dateParams, hospital_id: hospitalId } as any),
      ])
      const usersData = usersRes || {}
      const pointsData = pointsRes || {}
      const dailyData = (dailyRes as any)?.items || dailyRes || []
      const eventsData = (eventsRes as any)?.items || eventsRes || []
      const exchangesData = exchangesRes || {}

      setHospitalStats({
        totalUsers: (usersData as any).total_users || 0,
        activeUsers: (usersData as any).active_users || 0,
        totalPoints: (pointsData as any).total_balance || 0,
        todayPoints: (pointsData as any).today_points || 0,
        monthPoints: (pointsData as any).month_points || 0,
        pendingPoints: (pointsData as any).pending_points || 0,
        totalExchanges: (exchangesData as any).total_points || 0,
        pendingExchanges: (exchangesData as any).pending_count || 0,
        weeklyData: dailyData.map((d: any) => ({ date: d.date, points: d.points, count: d.count, users: d.count || 0 })),
        topEvents: mergeTopN(
          eventsData.map((e: any) => ({
            name: e.event_type_name || e.name || '',
            count: e.record_count ?? e.count ?? 0,
            value: e.total_points ?? 0
          })),
          9,
          '其他',
          'count'
        ).map((e: any) => ({
          name: e.name,
          count: e.count,
          value: e.value,
          points: e.value
        }))
      })
    } catch (e) {
      setHospitalStats(null)
    } finally {
      setHospitalLoading(false)
    }
  }

  const selectedHospital = hospitals.find(h => h.id === selectedHospitalId)

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
        <span className="ml-3 text-gray-500">加载中...</span>
      </div>
    )
  }

  return (
    <div className="space-y-6 overflow-x-hidden">
      {/* 页面标题和操作 */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            数据统计
            {isSupervisor && (
              <span className="flex items-center gap-1 text-sm font-normal text-green-600 bg-green-50 px-2 py-1 rounded-lg">
                <Shield size={14} /> 本机构
              </span>
            )}
          </h1>
          <p className="text-gray-500 text-sm mt-1">查看系统各项数据指标和分析</p>
        </div>

        {/* supervisor 视图提示：当前显示本机构数据 */}
        {isSupervisor && (
          <div className="flex-1 max-w-xl">
            <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3 flex items-start gap-3">
              <Shield className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-green-800">当前显示本机构数据</p>
                <p className="text-xs text-green-600 mt-0.5">
                  机构负责人仅可查看与统计本机构（{authUser?.hospitalName || '所属机构'}）的积分、人群分布、事件明细；全局数据需管理员权限。
                </p>
              </div>
            </div>
          </div>
        )}
        <div className="flex items-center space-x-3">
          {isAdmin && hospitals.length > 0 && (
            <div className="relative">
              <select
                value=""
                onChange={(e) => { if (e.target.value) openHospitalDrawer(e.target.value) }}
                className="px-4 py-2 border border-green-200 bg-green-50 text-green-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 cursor-pointer"
              >
                <option value="">切换机构查看</option>
                {hospitals.map((h: any) => (
                  <option key={h.id} value={h.id}>{h.name}</option>
                ))}
              </select>
            </div>
          )}
          <select
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            className="px-4 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option value="week">近7天</option>
            <option value="month">本月</option>
            <option value="quarter">本季度</option>
            <option value="custom">自定义日期范围</option>
          </select>
          {dateRange === 'custom' && (
            <div className="flex items-center space-x-2">
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
              />
              <span className="text-gray-400">至</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="px-3 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 text-sm"
              />
            </div>
          )}
          <button
            onClick={() => handleExport('excel')}
            disabled={exporting}
            className="flex items-center space-x-2 bg-green-700 text-white px-4 py-2 rounded-xl hover:bg-green-800 transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            <span>{exporting ? '导出中...' : '导出Excel'}</span>
          </button>
          <button
            onClick={() => handleExport('pdf')}
            disabled={exporting}
            className="flex items-center space-x-2 bg-white border border-gray-200 px-4 py-2 rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50"
          >
            <Download className="w-4 h-4" />
            <span>{exporting ? '导出中...' : '导出PDF'}</span>
          </button>
        </div>
      </div>

      {/* 核心指标卡片 */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white rounded-2xl shadow-md p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-3 bg-green-100 rounded-xl">
              <Users className="w-6 h-6 text-green-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-800">{stats.totalUsers.toLocaleString()}</p>
          <p className="text-sm text-gray-500 mt-1">总用户数</p>
          <div className="flex items-center mt-2 text-sm text-green-600">
            <TrendingUp className="w-4 h-4 mr-1" />
            +{((stats.activeUsers / stats.totalUsers) * 100).toFixed(1)}% 活跃
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-md p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-3 bg-blue-100 rounded-xl">
              <Activity className="w-6 h-6 text-blue-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-800">{stats.totalPoints.toLocaleString()}</p>
          <p className="text-sm text-gray-500 mt-1">积分累计</p>
          <div className="flex items-center mt-2 text-sm text-green-600">
            <TrendingUp className="w-4 h-4 mr-1" />
            +12.5% 较上期
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-md p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-3 bg-orange-100 rounded-xl">
              <Gift className="w-6 h-6 text-orange-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-800">{stats.totalExchanges.toLocaleString()}</p>
          <p className="text-sm text-gray-500 mt-1">兑换次数</p>
          <div className="flex items-center mt-2 text-sm text-orange-600">
            <span>{stats.pendingExchanges} 待处理</span>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-md p-6">
          <div className="flex items-center space-x-3 mb-4">
            <div className="p-3 bg-purple-100 rounded-xl">
              <Calendar className="w-6 h-6 text-purple-600" />
            </div>
          </div>
          <p className="text-3xl font-bold text-gray-800">{stats.todayPoints.toLocaleString()}</p>
          <p className="text-sm text-gray-500 mt-1">今日积分</p>
          <div className="flex items-center mt-2 text-sm text-gray-600">
            <span>{stats.activeUsers > 0 ? Math.round(stats.todayPoints / stats.activeUsers * 100) / 100 : 0} 人均</span>
          </div>
        </div>
      </div>

      {/* 图表区域 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 积分趋势 */}
        <div className="bg-white rounded-2xl shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-800">积分趋势</h3>
            <BarChart3 className="w-5 h-5 text-gray-400" />
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.weeklyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" stroke="#9CA3AF" fontSize={12} />
                <YAxis stroke="#9CA3AF" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                />
                <Bar
                  dataKey="points"
                  fill="#2B5D3A"
                  radius={[4, 4, 0, 0]}
                  name="积分"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 每日记录数趋势 */}
        <div className="bg-white rounded-2xl shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-800">每日记录数趋势</h3>
            <Activity className="w-5 h-5 text-gray-400" />
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.weeklyData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="date" stroke="#9CA3AF" fontSize={12} />
                <YAxis stroke="#9CA3AF" fontSize={12} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#4A90E2"
                  strokeWidth={3}
                  dot={{ fill: '#4A90E2', strokeWidth: 2, r: 4 }}
                  name="记录数"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 事件类型分布 */}
        <div className="bg-white rounded-2xl shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-800">事件类型分布</h3>
            <PieChartIcon className="w-5 h-5 text-gray-400" />
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={stats.topEvents}
                  cx="50%"
                  cy="50%"
                  innerRadius={50}
                  outerRadius={90}
                  paddingAngle={3}
                  dataKey="value"
                  nameKey="name"
                  label={({ name, percent }) =>
                    `${name} ${(percent * 100).toFixed(0)}%`
                  }
                  labelLine={{ stroke: '#9CA3AF', strokeWidth: 1 }}
                >
                  {stats.topEvents.map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* 机构分布 */}
        <div className="bg-white rounded-2xl shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-800">机构积分分布</h3>
            <BarChart3 className="w-5 h-5 text-gray-400" />
          </div>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats.hospitalStats} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis type="number" stroke="#9CA3AF" fontSize={12} />
                <YAxis
                  type="category"
                  dataKey="name"
                  stroke="#9CA3AF"
                  fontSize={12}
                  width={100}
                  tickFormatter={(value) =>
                    String(value).length > 8 ? String(value).slice(0, 8) + '...' : value
                  }
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: 'none',
                    borderRadius: '12px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
                  }}
                  formatter={(value: number) => [value.toLocaleString(), '积分']}
                />
                <Bar
                  dataKey="value"
                  fill="#F5A623"
                  radius={[0, 4, 4, 0]}
                  name="积分"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* 人群类型统计表（按用户类型累计统计） */}
      {crowdStats && crowdStats.length > 0 && (
        <div className="bg-white rounded-2xl shadow-md p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-bold text-gray-800">用户人群类型统计</h3>
            <Users className="w-5 h-5 text-gray-400" />
          </div>
          <p className="text-xs text-gray-400 mb-4">
            supervisor 看到的是本机构内部结构，admin 看到的是全局结构
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-left text-gray-500">
                  <th className="py-3 px-4 font-medium">机构名称</th>
                  <th className="py-3 px-4 font-medium">慢性病患者</th>
                  <th className="py-3 px-4 font-medium">慢性病高危人群</th>
                  <th className="py-3 px-4 font-medium">慢病+高危</th>
                  <th className="py-3 px-4 font-medium">其他</th>
                  <th className="py-3 px-4 font-medium">合计</th>
                </tr>
              </thead>
              <tbody>
                {crowdStats.map((row: any, idx: number) => {
                  const isGlobal = !row.hospital_id
                  return (
                    <tr key={row.hospital_id || 'global'} className={`border-b border-gray-100 hover:bg-gray-50 ${isGlobal ? 'bg-blue-50/40' : ''}`}>
                      <td className={`py-3 px-4 ${isGlobal ? 'font-bold text-gray-800' : 'text-gray-700'}`}>
                        {row.hospital_name}
                      </td>
                      <td className="py-3 px-4">
                        <span className="inline-block px-2 py-0.5 rounded bg-red-100 text-red-700 font-medium">
                          {row.chronic_disease ?? 0}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="inline-block px-2 py-0.5 rounded bg-orange-100 text-orange-700 font-medium">
                          {row.high_risk ?? 0}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="inline-block px-2 py-0.5 rounded bg-purple-100 text-purple-700 font-medium">
                          {row.chronic_and_high_risk ?? 0}
                        </span>
                      </td>
                      <td className="py-3 px-4">
                        <span className="inline-block px-2 py-0.5 rounded bg-gray-100 text-gray-600 font-medium">
                          {row.other ?? 0}
                        </span>
                      </td>
                      <td className={`py-3 px-4 ${isGlobal ? 'font-bold text-gray-800' : 'font-medium text-gray-700'}`}>
                        {row.total ?? 0}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* 详细数据表格 */}
      <div className="bg-white rounded-2xl shadow-md p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4">事件类型明细</h3>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">
                  事件类型
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">
                  参与次数
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">
                  积分总额
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">
                  积分占比
                  <div className="text-xs font-normal text-gray-400 mt-0.5">
                    当前表格内汇总
                  </div>
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {(() => {
                const totalInTable = stats.topEvents.reduce((s: number, e: any) => s + (e.points || 0), 0)
                return stats.topEvents.map((event, index) => {
                  const pct = totalInTable > 0 ? (event.points / totalInTable) * 100 : 0
                  return (
                    <tr key={event.name} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 font-medium text-gray-800">{event.name}</td>
                      <td className="px-6 py-4 text-gray-600">{event.count.toLocaleString()}</td>
                      <td className="px-6 py-4 text-green-600 font-medium">
                        {event.points.toLocaleString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-3">
                          <div className="flex-1 bg-gray-100 rounded-full h-2">
                            <div
                              className="h-2 rounded-full"
                              style={{
                                width: `${pct}%`,
                                backgroundColor: COLORS[index % COLORS.length]
                              }}
                            />
                          </div>
                          <span className="text-sm text-gray-600 w-14 text-right">
                            {pct.toFixed(1)}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  )
                })
              })()}
            </tbody>
          </table>
        </div>
      </div>

      {/* 县区 × 事件 积分矩阵（两个表：当前月 / 累计） */}
      <div className="bg-white rounded-2xl shadow-md p-4">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-gray-800">县区 × 人群 × 事件 积分矩阵</h2>
            <p className="text-xs text-gray-500 mt-1">横向滚动查看全部事件列</p>
          </div>
          <button
            onClick={exportMatrixExcel}
            disabled={!matrixMonth || !matrixCumulative}
            className="flex items-center space-x-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed text-white px-4 py-2 rounded-xl transition-colors text-sm"
          >
            <Download className="w-4 h-4" />
            <span>{exportingMatrix ? '导出中...' : '导出 Excel'}</span>
          </button>
        </div>
        <div className="space-y-6">
          <MatrixSection title="本月" matrix={matrixMonth} />
          <MatrixSection title="累计" matrix={matrixCumulative} />
        </div>
      </div>

      {/* 机构数据抽屉 */}
      {hospitalDrawerOpen && (
        <div className="fixed inset-0 z-50 flex justify-end" onClick={() => setHospitalDrawerOpen(false)}>
          {/* 遮罩 */}
          <div className="absolute inset-0 bg-black/30" />

          {/* 抽屉 */}
          <div
            className="relative w-full max-w-2xl bg-white shadow-2xl overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            {/* 抽屉头部 */}
            <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-green-100 rounded-xl">
                  <Building2 className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-800">
                    {selectedHospital?.name || '机构数据'}
                  </h2>
                  <p className="text-sm text-gray-500">
                    {hospitalLoading ? '加载中...' : `查看 ${selectedHospital?.name} 的详细数据（${dateRange === 'week' ? '近7天' : dateRange === 'month' ? '本月' : dateRange === 'quarter' ? '本季度' : '自定义日期'}）`}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setHospitalDrawerOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* 抽屉内容 */}
            <div className="p-6 space-y-6">
              {hospitalLoading ? (
                <div className="flex items-center justify-center py-20">
                  <Loader2 className="w-8 h-8 text-green-600 animate-spin" />
                </div>
              ) : hospitalStats ? (
                <>
                  {/* 核心指标 */}
                  <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                    {[
                      { label: '总用户数', value: hospitalStats.totalUsers, icon: Users, color: 'bg-green-100 text-green-600' },
                      { label: '积分余额', value: hospitalStats.totalPoints, icon: Activity, color: 'bg-blue-100 text-blue-600' },
                      { label: '兑换积分', value: hospitalStats.totalExchanges, icon: Gift, color: 'bg-orange-100 text-orange-600' },
                      { label: '待处理兑换', value: hospitalStats.pendingExchanges, icon: Calendar, color: 'bg-purple-100 text-purple-600' },
                    ].map((card) => (
                      <div key={card.label} className="bg-gray-50 rounded-2xl p-4">
                        <div className="flex items-center space-x-2 mb-2">
                          <div className={`p-2 rounded-lg ${card.color}`}>
                            <card.icon className="w-4 h-4" />
                          </div>
                        </div>
                        <p className="text-2xl font-bold text-gray-800">{Number(card.value).toLocaleString()}</p>
                        <p className="text-sm text-gray-500 mt-1">{card.label}</p>
                      </div>
                    ))}
                  </div>

                  {/* 人群类型统计（累计人数）—— 在核心指标下方、积分趋势上方 */}
                  <div className="bg-gray-50 rounded-2xl p-4">
                    <h3 className="text-md font-bold text-gray-800 mb-4">人群类型统计（累计人数）</h3>
                    {hospitalCrowdStats ? (
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                        <div className="bg-white rounded-xl p-4 text-center">
                          <p className="text-2xl font-bold text-orange-600">{hospitalCrowdStats.chronic_disease}</p>
                          <p className="text-xs text-gray-500 mt-1">慢性病患者</p>
                        </div>
                        <div className="bg-white rounded-xl p-4 text-center">
                          <p className="text-2xl font-bold text-yellow-600">{hospitalCrowdStats.high_risk}</p>
                          <p className="text-xs text-gray-500 mt-1">慢性病高危人群</p>
                        </div>
                        <div className="bg-white rounded-xl p-4 text-center">
                          <p className="text-2xl font-bold text-red-600">
                            {hospitalCrowdStats.chronic_disease + hospitalCrowdStats.high_risk}
                          </p>
                          <p className="text-xs text-gray-500 mt-1">慢病+高危</p>
                        </div>
                        <div className="bg-white rounded-xl p-4 text-center">
                          <p className="text-2xl font-bold text-gray-600">{hospitalCrowdStats.other}</p>
                          <p className="text-xs text-gray-500 mt-1">其他</p>
                        </div>
                        <div className="bg-white rounded-xl p-4 text-center">
                          <p className="text-2xl font-bold text-green-600">{hospitalCrowdStats.total}</p>
                          <p className="text-xs text-gray-500 mt-1">合计</p>
                        </div>
                      </div>
                    ) : (
                      <p className="text-center text-gray-400 py-4">暂无数据</p>
                    )}
                  </div>

                  {/* 积分趋势 */}
                  <div className="bg-gray-50 rounded-2xl p-4">
                    <h3 className="text-md font-bold text-gray-800 mb-4">积分趋势</h3>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={hospitalStats.weeklyData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e5e5" />
                          <XAxis dataKey="date" stroke="#9CA3AF" fontSize={11} />
                          <YAxis stroke="#9CA3AF" fontSize={11} />
                          <Tooltip
                            contentStyle={{ backgroundColor: '#fff', border: 'none', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                          />
                          <Bar dataKey="points" fill="#2B5D3A" radius={[4, 4, 0, 0]} name="积分" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* 事件类型分布 */}
                  <div className="bg-gray-50 rounded-2xl p-4">
                    <h3 className="text-md font-bold text-gray-800 mb-4">事件类型分布</h3>
                    <div className="h-48">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={hospitalStats.topEvents}
                            cx="50%"
                            cy="50%"
                            innerRadius={40}
                            outerRadius={75}
                            paddingAngle={3}
                            dataKey="value"
                            nameKey="name"
                            label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                          >
                            {hospitalStats.topEvents.map((_: any, index: number) => (
                              <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip
                            contentStyle={{ backgroundColor: '#fff', border: 'none', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                  </div>

                  {/* 事件明细 */}
                  <div className="bg-gray-50 rounded-2xl p-4">
                    <h3 className="text-md font-bold text-gray-800 mb-4">事件类型明细</h3>
                    <div className="space-y-3">
                      {(() => {
                        const totalInTable = hospitalStats.topEvents.reduce(
                          (s: number, e: any) => s + (e.points || 0), 0
                        ) || 1
                        return hospitalStats.topEvents.map((event: any, index: number) => {
                          const percent = ((event.points || 0) / totalInTable) * 100
                          return (
                            <div key={event.name} className="bg-white rounded-xl p-3">
                              <div className="flex items-center justify-between mb-2">
                                <span className="font-medium text-gray-800">{event.name}</span>
                                <div className="flex items-center gap-2">
                                  <span className="text-green-600 font-bold">{event.points.toLocaleString()} 积分</span>
                                  <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                                    {percent.toFixed(1)}%
                                  </span>
                                </div>
                              </div>
                              <div className="flex items-center gap-3">
                                <div className="flex-1 bg-gray-100 rounded-full h-2">
                                  <div
                                    className="h-2 rounded-full"
                                    style={{
                                      width: `${percent}%`,
                                      backgroundColor: COLORS[index % COLORS.length]
                                    }}
                                  />
                                </div>
                                <span className="text-sm text-gray-500">{event.count} 次</span>
                              </div>
                            </div>
                          )
                        })
                      })()}
                      {hospitalStats.topEvents.length === 0 && (
                        <p className="text-center text-gray-400 py-4">暂无数据</p>
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="text-center py-20 text-gray-400">加载失败</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Statistics
